import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';

export function useTechnicians() {
  const [technicians, setTechnicians] = useState<Database['public']['Tables']['technicians']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTechnicians() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('technicians')
          .select('*')
          .eq('company_id', user.id)
          .order('full_name');

        if (error) throw error;
        setTechnicians(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch technicians');
      } finally {
        setLoading(false);
      }
    }

    fetchTechnicians();
  }, []);

  const addTechnician = async (technicianData: Omit<Database['public']['Tables']['technicians']['Insert'], 'company_id' | 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('technicians')
        .insert([{ ...technicianData, company_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      setTechnicians(prev => [...prev, data].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateTechnician = async (id: string, technicianData: Partial<Database['public']['Tables']['technicians']['Update']>) => {
    try {
      const { data, error } = await supabase
        .from('technicians')
        .update(technicianData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setTechnicians(prev => 
        prev.map(tech => tech.id === id ? data : tech)
           .sort((a, b) => a.full_name.localeCompare(b.full_name))
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteTechnician = async (id: string) => {
    try {
      const { error } = await supabase
        .from('technicians')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTechnicians(prev => prev.filter(tech => tech.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return { technicians, loading, error, addTechnician, updateTechnician, deleteTechnician };
}

export function useTeamMembers() {
  const [teamMembers, setTeamMembers] = useState<Database['public']['Tables']['team_members']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTeamMembers() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('team_members')
          .select('*')
          .eq('company_id', user.id)
          .order('full_name');

        if (error) throw error;
        setTeamMembers(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch team members');
      } finally {
        setLoading(false);
      }
    }

    fetchTeamMembers();
  }, []);

  const addTeamMember = async (teamMemberData: Omit<Database['public']['Tables']['team_members']['Insert'], 'company_id' | 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('team_members')
        .insert([{ ...teamMemberData, company_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      setTeamMembers(prev => [...prev, data].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateTeamMember = async (id: string, teamMemberData: Partial<Database['public']['Tables']['team_members']['Update']>) => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .update(teamMemberData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setTeamMembers(prev => 
        prev.map(member => member.id === id ? data : member)
           .sort((a, b) => a.full_name.localeCompare(b.full_name))
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteTeamMember = async (id: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTeamMembers(prev => prev.filter(member => member.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return { teamMembers, loading, error, addTeamMember, updateTeamMember, deleteTeamMember };
}